import { getFromStorage, useStickyState } from "#src/hooks/useStickyState";
import { mergeDeepOverwriteLists } from "#src/utils/objects";
import {
  DefaultValues,
  Form,
  useHookForm as useForm,
  UseFormReturn,
  usePrevious,
} from "@validereinc/common-components";
import { NodeAPISavedFilterType, SavedFilterType } from "@validereinc/domain";
import { toExpandedObject } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { debounce } from "es-toolkit/compat";
import { fromJS, Map } from "immutable";
import React, {
  AriaAttributes,
  createContext,
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  convertFilterValuesToStoredFilters,
  countAppliedFilters,
  type FilterComparisonOperatorConfig,
  getDefaultValuesAndStoredFilters,
  reduceStoredFiltersToAppliedFilters,
  resetFilterValues,
} from "./FilterArea.helpers";
import styles from "./FilterArea.module.css";

const cx = classNames.bind(styles);

export type FilterAreaContextProps<TFilters extends Record<string, any>> = {
  /** the react hook form object */
  form: UseFormReturn<TFilters>;
  /** reset all filters wired up to the filter form. This resets the cache
   * (sessionStorage) as well and fires an onChange event. */
  handleResetFilters: () => Promise<void>;
  /** reset all filters wired up to the filter form. This does everything
   * resetting does as well as clear the applied saved filter, if any. */
  handleClearFilters: () => void;
  /** apply filter values of filters wired up to the filter form. The values
   * will be written to cache (sessionStorage). */
  handleSubmitFilters: () => void;
  /** get the filter values from cache (sessionStorage) */
  storedFilters: TFilters;
  /** set the filter values to the cache (sessionStorage) */
  setStoredFilters: (filters: Partial<TFilters>) => void;
  /** override the comparison operator used for specific filters. Object is flat
   * where key is the dot-separated name of the filter input and the value is an
   * operator to use. If operators are not specified for an input, the default
   * logic will be used.
   *
   * NOTE: this mainly affects the format of filters when they get saved. It
   * does not at the moment affect how the filters actually get applied against
   * a query. That entirely depends on the query implementation and the
   * underlying domain method logic.
   * // IMPROVE: we need a comprehensive filter abstraction that can help us do
   * this and more.
   *  */
  filterComparisonOperatorConfig?: FilterComparisonOperatorConfig;
  /** the number of filters that have been applied (i.e. in cache) */
  numOfFiltersApplied: number;
  /** a saved filter that has been applied if any */
  // @ts-expect-error TODO: fix filter typing - this Zod one is puzzling
  appliedSavedFilter: SavedFilterType<NodeAPISavedFilterType> | null;
  /** change the saved filter to apply */
  setAppliedSavedFilter: React.Dispatch<
    // @ts-expect-error TODO: fix filter typing - this Zod one is puzzling
    React.SetStateAction<SavedFilterType<NodeAPISavedFilterType> | null>
  >;
  // IMPROVE: saved views will go here
};

const FilterAreaContext = createContext<FilterAreaContextProps<
  Record<string, any>
> | null>(null);

const useFilterAreaContext = <TFilters extends Record<string, any>>() => {
  const ctx = useContext(
    FilterAreaContext as React.Context<FilterAreaContextProps<TFilters>>
  );

  if (!ctx) {
    throw new Error("Must be used within a FilterAreaProvider");
  }

  return ctx;
};

export type FilterAreaRootProps<TFilters extends Record<string, any>> =
  PropsWithChildren<{
    /** the cache key (for sessionStorage) to use for all the filters under this area */
    storageKey: string;
    /** the default values to use for all the filters wired up to the form */
    defaultValues?:
      | DefaultValues<TFilters>
      | (() => Promise<DefaultValues<TFilters>>);
    /** apply the default values to storage once its ready? */
    applyDefaultValues?: boolean;
    /** should stored filters overwrite default values when applying them? */
    shouldPrioritizeStoredFiltersWhenApplyingDefaultValues?: boolean;
    /** callback invoked when a change happens to any filter values */
    onChange?: (newFilters: Partial<TFilters>) => void;
    /** callback invoked when filters are cleared */
    onClear?: (newFilters: Record<string, never>) => void;
    /** callback invoked when filters are applied */
    onApply?: (newFilters: TFilters) => void;
    /** comparison operator overrides to use for specified filters */
    comparisonOperatorConfig?: FilterAreaContextProps<TFilters>["filterComparisonOperatorConfig"];
  }>;

/**
 * The base component of the FilterArea. Renders children as-is within a
 * context provider.
 */
const FilterAreaRoot = <TFilters extends Record<string, any>>({
  children,
  storageKey,
  defaultValues,
  applyDefaultValues,
  comparisonOperatorConfig,
  shouldPrioritizeStoredFiltersWhenApplyingDefaultValues = true,
  onChange,
  onClear,
  onApply,
}: FilterAreaRootProps<TFilters>) => {
  const [storedFilters, setStoredFilters] = useStickyState<TFilters>(
    {} as TFilters,
    storageKey
  );
  const [appliedSavedFilter, setAppliedSavedFilter] =
    // @ts-expect-error TODO: fix filter typing - this Zod one is puzzling
    useState<SavedFilterType<NodeAPISavedFilterType> | null>(null);
  const appliedDefaultValues = useRef<boolean>(false);
  const previousStorageKey = usePrevious(storageKey);

  const getAndApplyDefaultValues = useCallback(
    (storedFilters: TFilters) =>
      getDefaultValuesAndStoredFilters({
        defaultValues,
        storedFilters,
        prioritizeStoredFilters:
          shouldPrioritizeStoredFiltersWhenApplyingDefaultValues,
      }).then((values) => {
        // commit default values as stored filters, but only once
        if (applyDefaultValues && !appliedDefaultValues.current) {
          appliedDefaultValues.current = true;
          setStoredFilters(values);
        }

        return values;
      }),
    [defaultValues, applyDefaultValues, appliedDefaultValues]
  );

  const { getValues, reset, ...restForm } = useForm<TFilters>({
    defaultValues: () => getAndApplyDefaultValues(storedFilters),
    resetOptions: {
      keepDefaultValues: true,
      keepDirty: false,
      keepDirtyValues: false,
      keepValues: false,
    },
  });

  const numOfFiltersApplied = useMemo(
    () =>
      countAppliedFilters(reduceStoredFiltersToAppliedFilters(storedFilters)),
    [storedFilters]
  );

  const handleSetStoredFilters = useCallback(
    (newFilters: Partial<TFilters>) => {
      onChange?.(newFilters);
      setStoredFilters((oldFilters) =>
        mergeDeepOverwriteLists(
          Map(oldFilters),
          Map(toExpandedObject(newFilters))
        ).toJS()
      );
    },
    [onChange]
  );

  const handleResetFilters = () => {
    const allValues = getValues();
    const completelyResetValues =
      resetFilterValues(allValues) ?? ({} as TFilters);

    return getDefaultValuesAndStoredFilters({
      storedFilters: completelyResetValues,
      defaultValues,
      prioritizeStoredFilters: false,
    }).then((defaultValuesOnly) => {
      const finalResetValues = mergeDeepOverwriteLists(
        fromJS(completelyResetValues) as Map<string, any>,
        fromJS(defaultValuesOnly) as Map<string, any>
      ).toJS();

      // filter area is reset with default values on purpose
      reset(finalResetValues, { keepDefaultValues: false });
      // on the other hand, applied filters and consumers reflect fully cleared state
      setStoredFilters({} as TFilters);
      onChange?.({} as TFilters);
    });
  };

  const handleClearFilters = () => {
    setAppliedSavedFilter(null);
    handleResetFilters();
    onClear?.({});
  };

  const handleSubmitFilters = () => {
    const values = getValues();

    onChange?.(values);
    onApply?.(values);
    setStoredFilters(convertFilterValuesToStoredFilters<TFilters>(values));
  };

  // re-apply default values on storage key change
  useEffect(() => {
    // if the storage key hasn't changed or default values haven't been applied yet
    if (previousStorageKey === storageKey || !appliedDefaultValues.current)
      return;

    appliedDefaultValues.current = false;
    // when the key changes, the sticky state might not have updated in time -
    // so read directly from the storage location and apply it to the new
    // storage location
    getAndApplyDefaultValues(
      getFromStorage(storageKey, {} as TFilters, {
        storageSystem: window.localStorage,
      })
    ).then((values) => {
      // reset the filter area with all the new values
      reset(values, {
        keepDefaultValues: false,
      });
    });
  }, [
    previousStorageKey,
    storageKey,
    appliedDefaultValues,
    getAndApplyDefaultValues,
    getFromStorage,
    reset,
  ]);

  // NOTE: intentionally written for type assertion
  const FilterAreaContextTyped = FilterAreaContext as React.Context<
    FilterAreaContextProps<TFilters>
  >;

  return (
    <FilterAreaContextTyped.Provider
      value={{
        storedFilters,
        setStoredFilters: handleSetStoredFilters,
        numOfFiltersApplied,
        form: { getValues, reset, ...restForm },
        handleSubmitFilters,
        handleClearFilters,
        handleResetFilters,
        appliedSavedFilter,
        setAppliedSavedFilter,
        filterComparisonOperatorConfig: comparisonOperatorConfig,
      }}
    >
      {children}
    </FilterAreaContextTyped.Provider>
  );
};

export type FilterAreaContainerProps = PropsWithChildren<
  Pick<AriaAttributes, "aria-label"> &
    Partial<Pick<HTMLDivElement, "className">> & {
      style?: CSSProperties;
    } & {
      formProps?: Partial<Pick<HTMLDivElement, "className">> & {
        style?: CSSProperties;
      };
    }
>;

/**
 * The container for the filters of this filter area. Renders an actual element
 * classified as a filter region. Also renders the <Form /> to wrap the fitler
 * inputs. Make sure this container wraps all the filter inputs in your filter area.
 */
const FilterAreaContainer = ({
  children,
  className,
  style,
  formProps = {},
  ...restProps
}: FilterAreaContainerProps) => {
  const { form } = useFilterAreaContext();
  // pnpm resolves @types/react to separate instances for the app (Radix-augmented) and
  // packages/common-components (vanilla @types/react@17.0.93), making the two CSSProperties
  // types structurally incompatible at the index-signature level. The cast is safe at runtime.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formContainerStyle = formProps.style as any;

  return (
    <div
      role="region"
      aria-label="Filters"
      {...restProps}
      className={cx("container", className)}
      style={style}
    >
      <Form
        {...form}
        className={cx("form-container", formProps?.className)}
        style={formContainerStyle}
      >
        {children}
      </Form>
    </div>
  );
};

export type FilterAreaContentContextType = {
  /** meant to intercept the onChange handler of your filter input to apply a
   * filter on change. provide the value as-is and the name of the filter
   * input. */
  handleOnChange: (value: unknown, name: string) => void;
};

const FilterAreaContentContext =
  createContext<FilterAreaContentContextType | null>(null);

const useFilterAreaContentContext = () => {
  const ctx = useContext(FilterAreaContentContext);

  if (!ctx) {
    throw new Error("Must be used within a FilterAreaContentProvider");
  }

  return ctx;
};

export type FilterAreaContentProps = {
  children: ((props: FilterAreaContentContextType) => ReactNode) | ReactNode;
};

/**
 * Wrapper for all the filters of this filter area. Renders children as-is.
 * Provides a debounced onChange handler that can be wired up to your filter
 * inputs as you choose, to apply filters immediately on change. Don't use the
 * handler and use the apply and clear functions instead from the filter area
 * context if you want to explicitly apply and clear filters by triggers of your
 * choice.
 *
 * Additionally, use {@link useFilterAreaContentContext()} if you want access to
 * the debounced onChange handler anywhere under this component tree.
 */
const FilterAreaContent = <TFilters extends Record<string, any>>({
  children,
}: FilterAreaContentProps) => {
  const { storedFilters, setStoredFilters } = useFilterAreaContext<TFilters>();

  const handleOnChange = (value: unknown, name: string) => {
    //when filters with array values are cleared, we need to truly clear the filter value by setting it to the HTML standard empty string `""` rather than letting the `[]` empty array be set as the value
    if (Array.isArray(value) && !value.length) {
      // @ts-expect-error unknown expected
      setStoredFilters({
        [name]: "",
      });
    } else {
      // @ts-expect-error unknown expected
      setStoredFilters({
        [name]: value,
      });
    }
  };

  const debouncedHandleOnChange = useCallback(debounce(handleOnChange, 400), [
    storedFilters,
  ]);

  if (typeof children !== "function") return <>{children}</>;

  return (
    <FilterAreaContentContext.Provider
      value={{ handleOnChange: debouncedHandleOnChange }}
    >
      {children({ handleOnChange: debouncedHandleOnChange })}
    </FilterAreaContentContext.Provider>
  );
};

const Root = FilterAreaRoot;
const Container = FilterAreaContainer;
const Content = FilterAreaContent;

/**
 * This module and its components allows you to define a "filter area" and
 * wire-up filters within that persist to session storage which you can then
 * access anywhere in your component tree.
 *
 * @module FilterArea
 *
 * @example Basic usage with device filters
 * ```tsx
 * // Create your storage key
 * const { filterConfigStorageKey } = useStorageKey("devices-list");
 *
 * // Use in your component
 * <FilterArea.Root storageKey={filterConfigStorageKey}>
 *   <FilterArea.Container aria-label={`Filters for ${localize(`${AssetType.DEVICE}_plural`)}`}>
 *     <FilterDrawer.Root>
 *       <FilterArea.Content>
 *         {({ handleOnChange }) => (
 *           <div style={{ marginRight: 8, marginBottom: 0 }}>
 *             <TextInput
 *               name="name"
 *               label="Search"
 *               isLabelShown={false}
 *               placeholder={`Search ${localize(`${AssetType.DEVICE}_plural`)}...`}
 *               type="search"
 *               isInline
 *               onChange={(val) => handleOnChange(val, "name")}
 *             />
 *           </div>
 *         )}
 *       </FilterArea.Content>
 *       <FilterDrawer.Trigger />
 *       <FilterDrawer.Content>
 *         <DropdownInput
 *           label="Status"
 *           name="status"
 *           options={DEVICE_STATUS_OPTIONS}
 *           labelKey="label"
 *           valueKey="value"
 *           isMulti
 *           isFluid
 *           isSearchable
 *           isOptionalTextShown={false}
 *         />
 *       </FilterDrawer.Content>
 *     </FilterDrawer.Root>
 *   </FilterArea.Container>
 * </FilterArea.Root>
 * ```
 *
 * @example Advanced usage with facilities and saved filters
 * ```tsx
 * // Create your storage key
 * const { filterConfigStorageKey } = useStorageKey("facilities-list");
 *
 * // Use in your component
 * <FilterArea.Root storageKey={filterConfigStorageKey}>
 *   <FilterArea.Container aria-label={`Filters for ${localize(`${AssetType.FACILITY}_plural`)}`}>
 *     <FilterDrawer.Root>
 *       <FilterArea.Content>
 *         {({ handleOnChange }) => (
 *           <div style={{ marginRight: 8, marginBottom: 0 }}>
 *             <TextInput
 *               name={facilitySchemaKeys.name}
 *               label="Search"
 *               isLabelShown={false}
 *               placeholder={`Search ${localize("facility_plural")}...`}
 *               type="search"
 *               isInline
 *               onChange={(val) => handleOnChange(val, facilitySchemaKeys.name)}
 *             />
 *           </div>
 *         )}
 *       </FilterArea.Content>
 *       <FilterDrawer.Trigger />
 *       <FilterDrawer.Content>
 *         <FilterDrawer.SavedFilters
 *           config={{
 *             resourceType: Resources.FACILITY,
 *             savedFilterResourceAdapter: FacilityAdapter.savedFilters,
 *             tag: SavedFilterTag.LIST,
 *             filterBlacklist: ["period.from", "period.to"],
 *           }}
 *         />
 *         <FilterDrawer.SavedFiltersAppliedIndicator />
 *         <FacilitiesTableFilterAreaContent />
 *       </FilterDrawer.Content>
 *     </FilterDrawer.Root>
 *   </FilterArea.Container>
 * </FilterArea.Root>
 * ```
 *
 * @see {@link FilterAreaRoot} - The base provider component
 * @see {@link FilterAreaContainer} - The container wrapper for filters
 * @see {@link FilterAreaContent} - The content wrapper providing onChange handlers
 *
 * @exports FilterArea - The main compound component
 * @exports FilterAreaContainer - Container component for filter region
 * @exports FilterAreaContent - Content wrapper with onChange handlers
 * @exports FilterAreaRoot - Base provider component
 * @exports useFilterAreaContentContext - Hook to access filter content context
 * @exports useFilterAreaContext - Hook to access filter area context
 */
const FilterArea = {
  /**
   * The base component of the FilterArea. Renders children as-is within a
   * context provider. Not an actual DOM element.
   */
  Root,
  /**
   * The container for the filters of this filter area. Renders an actual element
   * classified as a filter region. Also renders the <Form /> to wrap the fitler
   * inputs. Make sure this container wraps all the filter inputs in your filter area.
   */
  Container,
  /**
   * Wrapper for all the filters of this filter area. Renders children as-is.
   * Provides a debounced onChange handler that can be wired up to your filter
   * inputs as you choose, to apply filters immediately on change. Don't use the
   * handler and use the apply and clear functions instead from the filter area
   * context if you want to explicitly apply and clear filters by triggers of your
   * choice.
   *
   * Additionally, use {@link useFilterAreaContentContext()} if you want access to
   * the debounced onChange handler anywhere under this component tree.
   */
  Content,
};

export {
  FilterArea,
  FilterAreaContainer,
  FilterAreaContent,
  FilterAreaRoot,
  useFilterAreaContentContext,
  useFilterAreaContext,
};
